import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty, authorizedFetch } from './client';

const SupplierCategorySchema = z.array(z.object({ key: z.string(), label: z.string() }));

const SupplierRowSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  is_active: z.coerce.number(),
  item_count: z.coerce.number().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type SupplierRow = z.infer<typeof SupplierRowSchema>;

const SupplierItemRowSchema = z.object({
  item_id: z.coerce.number(),
  item_name: z.string(),
  price: z.coerce.number().nullable(),
  effective_date: z.string(),
  updated_at: z.string().optional(),
  price_id: z.coerce.number().optional(),
  item_is_active: z.coerce.number().optional(),
  in_item_master: z.coerce.number().optional(),
});

export type SupplierItemRow = z.infer<typeof SupplierItemRowSchema>;

/** Rows merged from import failures (not yet in item master). */
export type SupplierPendingItemRow = SupplierItemRow & {
  pendingImport: true;
};

export type SupplierTableRow = SupplierItemRow & { pendingImport?: boolean };

export function isSupplierItemUnmatched(row: SupplierTableRow): boolean {
  if (row.pendingImport) return true;
  return Number(row.in_item_master) === 0;
}

const PriceHistoryRowSchema = z.object({
  id: z.coerce.number(),
  price: z.coerce.number().nullable(),
  effective_date: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type PriceHistoryRow = z.infer<typeof PriceHistoryRowSchema>;

export function useSupplierCategories() {
  return useQuery({
    queryKey: ['supplier-categories'],
    staleTime: 300_000,
    queryFn: () => apiFetch('/api/supplier-categories', SupplierCategorySchema),
  });
}

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    staleTime: 60_000,
    queryFn: () => apiFetch('/api/suppliers', z.array(SupplierRowSchema)),
  });
}

export function useSupplierNames() {
  return useQuery({
    queryKey: ['suppliers', 'names'],
    staleTime: 60_000,
    queryFn: async () => {
      const rows = await apiFetch('/api/suppliers', z.array(SupplierRowSchema));
      return rows.filter((r) => Number(r.is_active) === 1).map((r) => r.name);
    },
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch('/api/suppliers', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: { name?: string; is_active?: boolean } }) =>
      apiFetchEmpty(`/api/suppliers/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/suppliers/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useSupplierItems(supplierId: number | null) {
  return useQuery({
    queryKey: ['suppliers', supplierId, 'items'],
    enabled: supplierId != null,
    queryFn: () =>
      apiFetch(`/api/suppliers/${supplierId}/items`, z.array(SupplierItemRowSchema)),
  });
}

export function useSupplierPriceHistory(supplierId: number | null, itemId: number | null) {
  return useQuery({
    queryKey: ['suppliers', supplierId, 'items', itemId, 'price-history'],
    enabled: supplierId != null && itemId != null,
    queryFn: () =>
      apiFetch(
        `/api/suppliers/${supplierId}/items/${itemId}/price-history`,
        z.array(PriceHistoryRowSchema)
      ),
  });
}

export type AddSupplierPricePayload = {
  itemName?: string;
  itemId?: number;
  price: number;
  effective_date?: string;
};

export type UnmatchedItemError = {
  error: string;
  unmatchedItem: string;
};

export function useAddSupplierPrice(supplierId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddSupplierPricePayload) => {
      const res = await authorizedFetch(`/api/suppliers/${supplierId}/prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        unmatchedItem?: string;
        id?: number;
      };
      if (res.status === 422 && data.unmatchedItem) {
        const e = new Error(data.error || 'Item not found in item master');
        (e as Error & { unmatchedItem?: string }).unmatchedItem = data.unmatchedItem;
        throw e;
      }
      if (!res.ok) throw new Error(data.error || 'Failed to add price');
      return { id: data.id };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['suppliers', supplierId, 'items'] });
    },
  });
}

export function useDeleteSupplierItemPrices(supplierId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: number) =>
      apiFetchEmpty(`/api/suppliers/${supplierId}/items/${itemId}/prices`, { method: 'DELETE' }),
    onSuccess: (_data, itemId) => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['suppliers', supplierId, 'items'] });
      void qc.invalidateQueries({ queryKey: ['suppliers', supplierId, 'items', itemId, 'price-history'] });
    },
  });
}

export type SupplierPricesImportResult = {
  success: boolean;
  imported: number;
  skipped: number;
  unmatched: string[];
};

export function useImportSupplierPricesXlsx(supplierId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch(`/api/suppliers/${supplierId}/import/prices-xlsx`, {
        method: 'POST',
        body: fd,
      });
      const data = (await res.json().catch(() => ({}))) as SupplierPricesImportResult & { error?: string };
      if (!res.ok) throw new Error(data.error || 'Import failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['suppliers', supplierId, 'items'] });
    },
  });
}

export function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

export function formatSupplierDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? String(value).slice(0, 10);
}
